import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { REGIONS } from '@/constants';
import {
  getMerchantCountryKey,
  merchantRegion,
  getMerchantCountryConfig,
  getMerchantRegionConfig,
} from '@/selectors/merchant';
import { getOnboardingTypeFromWidgetCookie } from '@/selectors/widget';

export const getVariant = (
  { onboardingType, deploymentRegion, merchantCountry, state, countryConfig, regionConfig },
  variants,
  configurationKey,
) =>
  variants.reduce((acc, v) => {
    const matchesOnboardingType =
      v.onboardingTypes === '*' || v.onboardingTypes.includes(onboardingType);

    // Checks wether the passed country (or fallback) matches considering the current region
    const shouldRenderOn = country => {
      // If the container uses the new configuration key setup
      if (configurationKey) {
        return (
          (countryConfig && countryConfig[configurationKey] === v.variantId) ||
          (country === 'fallback' && regionConfig[configurationKey] === v.variantId)
        );
      } else {
        return (
          v.deploymentRegionsAndCountries === '*' || // * is more important then renders on fallback
          (v.deploymentRegionsAndCountries[deploymentRegion] &&
            v.deploymentRegionsAndCountries[deploymentRegion].includes(country))
        );
      }
    };

    const matchesCustomConditions =
      !v.customCondition ||
      v.customCondition({ onboardingType, deploymentRegion, merchantCountry, state });

    if (
      // Custom conditions have to match or be undefined
      matchesCustomConditions &&
      // It's the country variant
      ((matchesOnboardingType && shouldRenderOn(merchantCountry)) ||
        // Or if the variant was not found yet and it's a fallback
        (!acc && shouldRenderOn('fallback')))
    ) {
      // Sets the variant as the accumulator
      return v;
    } else {
      // Or returns whatever is already there
      return acc;
    }
  }, undefined);

function withStep(name, { id, description, variants, configurationKey }) {
  class Step extends React.Component {
    constructor(props) {
      super(props);

      this.id = id;
      this.description = description;
      this.variantContainer = getVariant(props, variants, configurationKey);
    }

    render() {
      const { variantContainer: VariantContainer } = this;

      return <VariantContainer {...this.props} />;
    }
  }

  Step.propTypes = {
    deploymentRegion: PropTypes.oneOf(Object.values(REGIONS)).isRequired,
    merchantCountry: PropTypes.string.isRequired,
    state: PropTypes.object.isRequired,
    onboardingType: PropTypes.string,
  };

  const WrappedStep = connect(state => ({
    onboardingType: getOnboardingTypeFromWidgetCookie(state),
    deploymentRegion: merchantRegion(state),
    merchantCountry: getMerchantCountryKey(state),
    countryConfig: getMerchantCountryConfig(state),
    regionConfig: getMerchantRegionConfig(state),
    state,
  }))(Step);

  WrappedStep.getFieldsToFill = props =>
    getVariant(props, variants, configurationKey).fieldsToFill || [];

  Step.displayName = `Step(${name})`;
  WrappedStep.displayName = `connect(Step(${name}))`;
  return WrappedStep;
}

export default withStep;
